import React from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '../contexts/LanguageContext';
import { activities } from '../data/activities';
import { HiCode } from 'react-icons/hi';
import { FaGraduationCap, FaRocket } from 'react-icons/fa';

const Activities: React.FC = () => {
  const { t } = useLanguage();
  
  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'club':
        return HiCode;
      case 'society':
        return FaGraduationCap;
      case 'startup':
        return FaRocket;
      default:
        return HiCode;
    }
  };

  const getTypeLabel = (type: string) => {
    return t(`activities.type.${type}`);
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'club':
        return 'from-blue-500 to-cyan-500';
      case 'society':
        return 'from-purple-500 to-pink-500';
      case 'startup':
        return 'from-green-500 to-emerald-500';
      default:
        return 'from-gray-500 to-gray-600';
    }
  };

  return (
    <section id="activities" className="py-20 px-6 bg-dark-bg">
      <div className="container mx-auto max-w-6xl">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="text-4xl font-bold mb-4">
            <span className="text-gradient">{t('activities.title')}</span>
          </h2>
          <p className="text-gray-400">{t('activities.subtitle')}</p>
        </motion.div>

        <div className="space-y-6">
          {activities.map((activity, index) => {
            const Icon = getTypeIcon(activity.type);
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-dark-card rounded-lg p-6 shadow-xl border-l-4"
                style={{
                  borderLeftColor: activity.type === 'club' ? '#3b82f6' : activity.type === 'society' ? '#a855f7' : '#10b981'
                }}
              >
                <div className="flex items-start gap-4">
                  <div className={`p-3 rounded-lg bg-gradient-to-br ${getTypeColor(activity.type)}`}>
                    <Icon className="text-2xl text-white" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="text-xl font-bold text-white">{activity.organization}</h3>
                          <span className={`px-2 py-1 rounded text-xs bg-gradient-to-r ${getTypeColor(activity.type)} bg-clip-text text-transparent border border-current`}>
                            {getTypeLabel(activity.type)}
                          </span>
                        </div>
                        <p className="text-lg text-gray-300 mb-1">{activity.position}</p>
                        <p className="text-sm text-gray-400">{activity.period}</p>
                      </div>
                    </div>
                    <p className="text-gray-300 mt-2">{activity.description}</p>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Activities;
